<template>
  <div class="promocode-wrapper d-flex fl-col fl-j-c fl-a-c">
    <div class="w-full d-flex fl-j-c fl-a-c options-wrapper gap-2">
      <SButton
        type="button"
        size="lg"
        class="flex-1 h-14"
        :variant="
          paymentType === PaymentMethods.POS_PROMO_CODE ? 'default' : 'outline'
        "
        @click="updatePaymentType(PaymentMethods.POS_PROMO_CODE)"
      >
        {{ $t("promocode.promocode") }}
      </SButton>
      <SButton
        type="button"
        size="lg"
        class="flex-1 h-14"
        :variant="
          paymentType === PaymentMethods.POS_GIFT_CARD ? 'default' : 'outline'
        "
        @click="updatePaymentType(PaymentMethods.POS_GIFT_CARD)"
      >
        {{ $t("promocode.gift-card") }}
      </SButton>
      <SButton
        type="button"
        size="lg"
        class="flex-1 h-14"
        :variant="
          paymentType === PaymentMethods.POS_MEMBERSHIP ? 'default' : 'outline'
        "
        @click="updatePaymentType(PaymentMethods.POS_MEMBERSHIP)"
      >
        {{ $t("promocode.membership") }}
      </SButton>
    </div>
    <div class="inputs-wrapper">
      <button
        class="el-button booking-button scan-promocode-button"
        @click="openScanPromocodeModal"
      >
        <div class="d-flex fl-s-b fl-a-c">
          <p>{{ $t("button.scan-voucher") }}</p>
          <ScanQrCodeIcon
            style="--s-icon-size: 40px"
            aria-label="Scanner icon"
          />
        </div>
      </button>
      <div class="scanner-divider">
        <SSeparator />
      </div>

      <div class="mx-auto flex w-[96%] gap-2.5">
        <SInputGroup class="h-20 min-w-0 flex-1">
          <SInputGroupInput
            class="h-full text-[26px]"
            :placeholder="inputPlaceholder"
            :model-value="promocode"
            @update:model-value="(v) => (promocode = String(v))"
            data-cy="promocode-input"
          />
          <SInputGroupAddon align="inline-end">
            <SvgIcon
              :iconContent="editPenIconContent"
              :iconAlt="editPenIconAlt"
              :iconStyle="editPenIconStyle"
            />
          </SInputGroupAddon>
        </SInputGroup>
        <SButton
          type="button"
          class="h-20 w-[30%] shrink-0 text-3xl"
          :loading="isLoading"
          :disabled="!isPromocodeLengthValid"
          @click="applyPromocode"
          data-cy="apply-promocode-button"
        >
          {{ $t("promocode.apply") }}
        </SButton>
      </div>

      <div
        style="
          margin-top: 10px;
          width: 96%;
          margin-left: auto;
          margin-right: auto;
        "
      >
        <div
          class="codes-container"
          style="display: flex; flex-direction: row; flex-wrap: wrap"
        >
          <div
            v-for="stat in appliedCodesStats"
            :key="stat.code"
            class="one-code-container d-flex fl-col fl-j-c"
            style="
              width: 47%;
              margin-bottom: 5px;
              margin-top: 5px;
              margin-right: 2%;
            "
          >
            <div
              class="d-flex flex-row fl-j-c"
              style="
                height: 45px;
                line-height: 45px;
                background-color: #efefef;
                display: flex;
                flex-direction: row;
                justify-content: space-between;
              "
            >
              <div style="text-align: center; width: 80%">
                <p>
                  {{ stat.code }}
                </p>
              </div>
              <div
                style="
                  cursor: pointer;
                  text-align: center;
                  width: 15%;
                  font-size: 25px;
                "
                data-cy="remove-promocode-btn"
                @click="removeCodePayment(stat.code)"
              >
                <p>X</p>
              </div>
            </div>
            <div class="code-results d-flex fl-col fl-j-c">
              <div style="margin-top: 5px">
                <p>
                  {{
                    -(stat.ttlDiscountOrPayment / 100).toFixed(2) +
                    " " +
                    currency
                  }}
                </p>
              </div>
              <div style="margin-top: 5px">
                <p v-if="Number(stat.remaining) >= 0">
                  {{ $t("promocode.remaining-amount") }}
                  {{
                    (Number(stat.remaining) / 100).toFixed(2) + " " + currency
                  }}
                </p>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
    <SDialog v-model:open="scanPromocodeModal">
      <SDialogContent
        class="promocode-wrapper scan-promocode-modal !w-[620px] !max-w-[620px]"
        @open-auto-focus.prevent
      >
        <SDialogHeader>
          <SDialogTitle class="sr-only">
            {{ $t("promocode.please-scan-voucher") }}
          </SDialogTitle>
        </SDialogHeader>
        <div class="content">
          <div class="flex w-full items-center justify-center gap-4">
            <ScanQrCodeIcon
              style="--s-icon-size: 64px"
              aria-label="Scanner icon"
              class="shrink-0"
            />
            <p class="text-base font-semibold text-foreground leading-snug">
              {{ $t("promocode.please-scan-voucher") }}
            </p>
          </div>
        </div>
      </SDialogContent>
    </SDialog>
  </div>
</template>
<script lang="ts">
import BookingModule from "@/store/modules/booking";
import { computed, defineComponent, ref, watch, watchEffect } from "vue";
import { useI18n } from "vue-i18n";
import NotificationService from "@/services/notification.service";
import { LogLevel, LogService } from "@/services/logging";
import editPen from "@/assets/images/svg/edit-pen-rectangle.svg";
import roundCheckIcon from "@/assets/images/svg/green-round-check.svg";
import { Currencies } from "@/constants/currency";
import {
  AccountingState,
  PaymentMethods,
  PromocodeCampaignType,
} from "@/types/db/accounting";
import { sumArray } from "@/utils/helpers/common";
import ScannerModule from "@/store/modules/scanner";
import { IProductSlotBooking } from "@/types/db/product-slot-booking";
import {
  extractCodeFromUrl,
  getPromocodeInputPlaceholder,
  getPromocodePaymentType,
  processPromocode,
  removeCodePayment,
} from "@/utils/vue-helpers/promocode";
import { getSumOfCodeGeneratedAccountingsValues } from "@/utils/vue-helpers/new-accountings";
import { getPssVoucherByToken } from "@/api/gql/product-slot-state";
import POSConfigModule from "@/store/modules/pos-config";
import {
  SButton,
  SDialog,
  SDialogContent,
  SDialogHeader,
  SDialogTitle,
  SInputGroup,
  SInputGroupAddon,
  SInputGroupInput,
  SSeparator,
} from "@smeetz/ui-core";
import { ScanQrCodeIcon } from "@smeetz/ui-icons";
import {
  getUntypedBookingByUuid,
  isBookingInDexie,
} from "@/utils/helpers/booking";

export default defineComponent({
  name: "Promocode",
  components: {
    SButton,
    SDialog,
    SDialogContent,
    SDialogHeader,
    SDialogTitle,
    SInputGroup,
    SInputGroupAddon,
    SInputGroupInput,
    SSeparator,
    ScanQrCodeIcon,
  },
  setup() {
    const { t } = useI18n({});
    const editPenIconStyle = "width: 30px; height: 30px";
    const editPenIconContent = editPen;
    const editPenIconAlt = "Edit";
    const roundCheckIconStyle = "width: 32px; height: 32px; margin-right: 10px";
    const roundCheckIconContent = roundCheckIcon;
    const roundCheckIconAlt = "Check";

    const isEditOrder = ref(false);
    const promocode = ref(
      getExistingPromocode() /*BookingModule.appliedPromocode*/
    );
    const isLoading = ref(false);

    const displaySuccessMsg = computed(() => {
      // We return true if the code is applied successfully
      // the code is applied successfully if it has generated accountings
      if (getSumOfCodeGeneratedAccountingsValues(promocode.value) > 0) {
        return true;
      } else {
        return false;
      }
    });

    const isPromocodeApplied = computed(() => {
      return BookingModule.isPromocodeApplied;
    });
    const isPromocodeLengthValid = computed(() => {
      return promocode.value.length >= 5;
    });

    const appliedCodesStats = ref<
      { code: string; ttlDiscountOrPayment: number; remaining?: number }[]
    >([]);

    // updates appliedCodesStats
    watchEffect(async () => {
      const stats: {
        code: string;
        ttlDiscountOrPayment: number;
        remaining?: number;
      }[] = [];
      const booking = BookingModule.selectedBooking as IProductSlotBooking;
      const codePendingAccountings = booking.accountings.filter(
        (acc) =>
          acc.state === AccountingState.Pending &&
          !acc.refund &&
          !acc.id /*not coming from backend*/ &&
          (acc.promocode || acc.promoCodeUuid || acc.promoCodeId)
      );

      if (codePendingAccountings && codePendingAccountings.length) {
        const codes = [
          ...new Set(
            codePendingAccountings.map(
              (acc) => acc.promocode || acc.promoCodeUuid
            )
          ),
        ];
        // console.log("applied codes");
        // console.log(codes);

        for (let i = 0; i < codes.length; i++) {
          const accEntriesValues = codePendingAccountings
            .filter(
              (acc) =>
                acc.promocode === codes[i] || acc.promoCodeUuid === codes[i]
            )
            .map((acc) => acc.accountingEntriesList)
            .flat()
            .map((ae) => ae.value);
          const sum = sumArray(accEntriesValues);

          // TODO POS: Rewrite this
          const promocodeRemainingValue = await getPromocodeRemainingValue(
            String(codes[i])
          );
          const promocodeOldDiscountValues =
            await getPromocodeOldDiscountValues();
          const remain = promocodeRemainingValue + promocodeOldDiscountValues;

          stats.push({
            code: String(codes[i]),
            ttlDiscountOrPayment: sum,
            remaining: remain,
          });
        }
      }
      appliedCodesStats.value = stats;
    });

    const currency = computed(() => {
      return BookingModule.selectedBooking?.currency || Currencies.CHF;
    });

    const newLogicCodeResult = computed(() => {
      // Here we return the pending accountings values related to this applied code that were recently created when user applied the code
      const createdAccountings =
        BookingModule.selectedBooking?.accountings.filter(
          (acc) =>
            acc.state === AccountingState.Pending &&
            !acc.refund &&
            (acc.promocode === promocode.value ||
              acc.promoCodeUuid === promocode.value)
        );
      if (createdAccountings && createdAccountings.length) {
        return sumArray(
          createdAccountings
            .map((acc) => acc.accountingEntriesList)
            .flat()
            .map((ae) => ae.value)
        );
      }
      return 0;
      // const appliedCode = BookingModule.appliedPromocodes.find((code) => code.promocode.code === promocode.value);
      // if (!appliedCode) return 0;
      // return sumArray(getPromocodePaymentValue(BookingModule.selectedBooking?.pssList || [], appliedCode).map((it) => it.promocodeDiscountValue));
    });
    const newLogicCodeResultText = computed(() => {
      return currency.value + " " + (newLogicCodeResult.value / 100).toFixed(2);
    });

    async function getPromocodeRemainingValue(code?: string) {
      const dt1 = new Date();
      let appCode = promocode.value;
      if (code) {
        appCode = code;
      }

      if (!promocode.value) {
        return 0;
      }
      const currentPromocode = BookingModule.appliedPromocodes.find(
        (p) => p.promocode.code === appCode
      );
      if (!currentPromocode) {
        return 0;
      }
      let flatValue = 0;
      let pendingValue = 0;
      if (currentPromocode) {
        flatValue = currentPromocode.promocode.flatValue;
        pendingValue = Number(currentPromocode.promocode.pendingValue);
      }
      let codeResult = 0;
      // Payment code result is the sum of accEntries recently generated by this code
      let codeAccountings = BookingModule.selectedBooking?.accountings.filter(
        (acc) =>
          (acc.promocode === appCode || acc.promoCodeUuid === appCode) &&
          !acc.refund
      );

      if (codeAccountings && codeAccountings.length) {
        // so we have accountings generated by this code
        // lets exclude that ones that are already synched in backend and are already included in promocode pending value
        const syncedBooking = (
          await getUntypedBookingByUuid(BookingModule.selectedBookingUUID)
        )?.booking;
        let oldCodeAccountingsUuid: string[] = [];
        if (syncedBooking) {
          // So we're in an Edit order
          // lets get old accountings generated by this code
          const oldCodeAccountings =
            BookingModule.selectedBooking?.accountings.filter(
              (acc) =>
                acc.promocode === appCode || acc.promoCodeUuid === appCode
            );
          if (oldCodeAccountings && oldCodeAccountings.length) {
            oldCodeAccountingsUuid = oldCodeAccountings.map((acc) =>
              String(acc.uuid)
            );
          }
        }

        if (oldCodeAccountingsUuid && oldCodeAccountingsUuid.length) {
          // lets exclude old accountings from codeAccountings array
          codeAccountings = codeAccountings.filter(
            (acc) => !oldCodeAccountingsUuid.includes(String(acc.uuid))
          );
        }

        codeResult = sumArray(
          codeAccountings
            .map((acc) => acc.accountingEntriesList)
            .flat()
            .map((ae) => ae.value)
        );
      }

      const amountAvailable = flatValue - pendingValue - codeResult;
      return amountAvailable;
    }

    const paymentType = ref(PaymentMethods.POS_PROMO_CODE);
    const successMessage = ref("");
    const inputPlaceholder = computed(() =>
      getPromocodeInputPlaceholder(paymentType.value)
    );

    const appliedPromocodeType = computed(() => {
      return BookingModule.appliedPromocode?.promocode.promoCodeType;
    });

    function updatePaymentType(type: PaymentMethods) {
      paymentType.value = type;
    }

    function setSuccessMessage(promocodeType: PromocodeCampaignType) {
      switch (promocodeType) {
        case PromocodeCampaignType.TYPE_PROMO_CODE:
          successMessage.value = t("promocode.promo-code-applied");
          break;
        case PromocodeCampaignType.TYPE_GIFT_CARD_SOLD:
          successMessage.value = t("promocode.gift-card-applied");
          break;
        case PromocodeCampaignType.TYPE_VOUCHER_SOLD:
          successMessage.value = t("promocode.voucher-applied");
          break;
        case PromocodeCampaignType.TYPE_MEMBERSHIP_SOLD:
          successMessage.value = t("promocode.membership-applied");
      }
    }

    watchEffect(async () => {
      if (BookingModule.selectedBookingUUID) {
        isEditOrder.value = await isBookingInDexie(
          BookingModule.selectedBookingUUID
        );
      }
    });

    if (isPromocodeApplied.value && appliedPromocodeType.value) {
      setSuccessMessage(appliedPromocodeType.value);
    }

    const scanPromocodeModal = ref(false);
    const ScannerTog = computed(() => {
      return ScannerModule.tog;
    });

    watch(
      ScannerTog,
      async () => {
        if (ScannerModule.value && scanPromocodeModal.value) {
          LogService.info(
            `Promocode: scanner triggered, value = ${ScannerModule.value}`
          );
          // The scanned valued can be a json object, a url (old logic), or a simple token
          // Check if the voucherCode is a URL
          const scannedValue = ScannerModule.value;
          const isUrl =
            String(scannedValue).startsWith("http://") ||
            String(scannedValue).startsWith("https://") ||
            String(scannedValue).startsWith("http//") ||
            String(scannedValue).startsWith("https//");

          let code = "";
          if (isUrl) {
            LogService.info(`Promocode: scanned value is in url format`);
            console.log(`Promocode: scanned value is in url format`);
            // We keep the old logic of extracting the code from the url scanned
            code = extractCodeFromUrl(ScannerModule.value);
            LogService.info(
              `Promocode: extracted from qrcode string code: ${code}`
            );
            if (code && code.length >= 5) {
              promocode.value = code;
              LogService.info(
                `Promocode: going to process scanned promocode: ${promocode.value}`
              );
              await applyPromocode();
              LogService.info(
                `Promocode: Done processing scanned promocode: ${promocode.value}`
              );
            }
            scanPromocodeModal.value = false;
            return;
          }

          LogService.info(
            `Promocode: scanned value is not in url format. Checking if it is json object.`
          );

          // Check if the scanned value is actually a json object
          try {
            const scannedJsonCode = JSON.parse(scannedValue);
            LogService.info(
              `Promocode: scanned value is in json format attached.`,
              { params: scannedJsonCode }
            );
            // Check if it's in the expected format
            if (
              scannedJsonCode &&
              scannedJsonCode.code &&
              scannedJsonCode.token
            ) {
              LogService.info(
                `Promocode: scanned value is exactly in the expected format.`,
                { params: scannedJsonCode }
              );
              // Lets proceed applying the code
              code = scannedJsonCode.code;
              promocode.value = scannedJsonCode.code;
              LogService.info(
                `Promocode: going to process scanned promocode: ${promocode.value}`
              );
              await applyPromocode();
              LogService.info(
                `Promocode: Done processing scanned promocode: ${promocode.value}`
              );
              scanPromocodeModal.value = false;
            } else {
              LogService.info(
                `Promocode: scanned value is not in the expected format`,
                { params: scannedJsonCode }
              );
              // Lets notify the user that code couldn't be scanned
              NotificationService.showCustomMessage(
                t("promocode.could-not-scan"),
                "error"
              );
            }
          } catch (err) {
            // Couldn't parse the scanned value into a json object
            LogService.info(
              `Promocode: scanned value is neither a url nor a valid json object. Checking if it's a simple token.`
            );

            const token = String(scannedValue).trim();
            if (token) {
              try {
                const results = await getPssVoucherByToken(
                  token,
                  POSConfigModule.groupId as number
                );

                if (results && results[0] && results[0].promocode) {
                  const voucherCode =
                    results[0].promocode.code ||
                    results[0].promocode.codeUnique ||
                    "";
                  LogService.info(
                    `Promocode: successfully retrieved voucher code from token: ${token}, code: ${voucherCode}`
                  );
                  promocode.value = voucherCode;
                  LogService.info(
                    `Promocode: going to process retrieved promocode: ${promocode.value}`
                  );
                  await applyPromocode();
                  LogService.info(
                    `Promocode: Done processing retrieved promocode: ${promocode.value}`
                  );
                  scanPromocodeModal.value = false;
                } else {
                  LogService.info(
                    `Promocode: no voucher code found for token: ${token}`
                  );
                  NotificationService.showCustomMessage(
                    t("promocode.could-not-scan"),
                    "error"
                  );
                }
              } catch (tokenErr) {
                LogService.info(
                  `Promocode Error: failed to retrieve voucher code from token: ${token}`,
                  { param: { token, tokenErr } },
                  LogLevel.Error
                );
                NotificationService.showCustomMessage(
                  t("promocode.could-not-scan"),
                  "error"
                );
              }
            }
          }
        }
      },
      { deep: true }
    );

    /**
     * This function return the last used promocode if there are any
     */
    function getExistingPromocode(): string {
      if (!isEditOrder.value) return "";
      // if the booking has a promocode accounting, then we get the code  showing
      const accountings = BookingModule.selectedBooking?.accountings || [];
      let code = "";
      for (let i = 0; i < accountings.length; i++) {
        if (
          (accountings[i].promoCodeId || accountings[i].promoCodeUuid) &&
          accountings[i].state === AccountingState.Completed &&
          accountings[i].refund !== 1 &&
          accountings[i].promocode
        ) {
          code = accountings[i].promocode || "";
        }
      }
      return code;
    }

    /**
     * this function returns the promocode discount value of the original pulled booking
     */
    async function getPromocodeOldDiscountValues() {
      const syncedBooking = (
        await getUntypedBookingByUuid(BookingModule.selectedBookingUUID)
      )?.booking as IProductSlotBooking;
      if (!isEditOrder.value || !syncedBooking) {
        return 0;
      }
      const oldPromocodeAccountingEntries = syncedBooking.accountings
        .filter(
          (acc) =>
            acc.promoCodeId &&
            acc.state === AccountingState.Completed &&
            acc.refund !== 1
        )
        .map((acc) => acc.accountingEntriesList)
        .flat();
      return sumArray(oldPromocodeAccountingEntries.map((ae) => ae.value));
    }

    async function applyPromocode() {
      if (isLoading.value) return;
      isLoading.value = true;
      try {
        await processPromocode(promocode.value);

        // check if the promocode was applied
        const appliedCode = BookingModule.appliedPromocodes.find(
          (p) => p.promocode.code === promocode.value
        );
        if (appliedCode) {
          // the promocode was succesfully applied
          // let's update the payment type UI
          const promocodePaymentType = getPromocodePaymentType(
            appliedCode.promocode.promoCodeType
          );
          if (promocodePaymentType) {
            paymentType.value = promocodePaymentType;
          }
        }
      } finally {
        isLoading.value = false;
      }
    }

    return {
      displaySuccessMsg,
      isPromocodeApplied,
      promocode,
      isLoading,
      editPenIconStyle,
      editPenIconContent,
      editPenIconAlt,
      isPromocodeLengthValid,
      roundCheckIconStyle,
      roundCheckIconAlt,
      roundCheckIconContent,
      t,
      paymentType,
      successMessage,
      updatePaymentType,
      PaymentMethods,
      appliedPromocodeType,
      inputPlaceholder,
      setSuccessMessage,
      isEditOrder,
      scanPromocodeModal,
      newLogicCodeResultText,
      appliedCodesStats,
      currency,
      applyPromocode,
      removeCodePayment,
    };
  },
  methods: {
    openScanPromocodeModal() {
      this.scanPromocodeModal = true;
    },
  },
});
</script>

<style lang="scss" scoped>
@use "@/styles/variables.scss" as *;

.promocode-wrapper {
  // padding-top: 80px;
  .btn-height {
    height: 50px;
  }

  .inputs-wrapper {
    width: 80%;

    .scan-promocode-button {
      display: block;
      width: 96%;
      height: 80px;
      margin: 0 auto;
      padding: 0 40px;
      background-color: var(--primary-smeetz);
      color: white;
    }

    .scanner-divider {
      margin: 0 auto;
      width: 96%;
    }
  }

  .promocode-discount-result-wrapper {
    margin-top: 50px;
    width: 80%;

    .success-text {
      width: 90%;
      margin-left: auto;
      margin-right: auto;
      margin-bottom: 20px;
      color: #008000;
      font-weight: 600;
    }
    .discount-text {
      width: 90%;
      margin-left: auto;
      margin-right: auto;
      height: 110px;
      border-radius: 6px;
      background-color: #e5e5e5;
      font-size: 40px;
      color: $color-dark-grey;
    }
  }

  .options-wrapper {
    padding-bottom: 30px;
  }
}

.remaining-amount {
  margin-left: 20px;
}

.remaining-amount-text {
  font-size: 0.9rem;
  font-weight: bolder;
  background-color: $color-background-green;
  border-radius: 5px;
  padding: 3px 15px;
}

.scan-promocode-modal .content {
  padding-bottom: 30px;
}
</style>
